import { jwtDecode } from 'jwt-decode';

/**
 * JWT payload structure
 */
interface JWTPayload {
  sub: string;           // User ID
  email: string;
  tenant_id?: string | number;
  exp: number;           // Expiration (Unix timestamp in seconds)
  iat: number;           // Issued at
  [key: string]: unknown;
}

/**
 * Decode JWT and extract payload
 * 
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeToken = (token: string): JWTPayload | null => {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (error) {
    console.error('[JWT] Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * 
 * @param token - JWT token
 * @param bufferSeconds - Consider expired this many seconds early (default: 0)
 * @returns True if token is expired
 */
export const isTokenExpired = (token: string, bufferSeconds = 0): boolean => {
  const payload = decodeToken(token);
  if (!payload) return true;
  
  const now = Date.now() / 1000;
  return payload.exp - bufferSeconds < now;
};

/**
 * Get time until token expiry in seconds
 * 
 * @param token - JWT token
 * @returns Seconds until expiry (0 if already expired)
 */
export const getTimeUntilExpiry = (token: string): number => {
  const payload = decodeToken(token);
  if (!payload) return 0;
  
  const now = Date.now() / 1000;
  return Math.max(0, payload.exp - now);
};

/**
 * Check if token should be refreshed (within 5 minutes of expiry)
 * 
 * @param token - JWT token
 * @returns True if token should be refreshed
 */
export const shouldRefreshToken = (token: string): boolean => {
  const REFRESH_BUFFER = 5 * 60; // 5 minutes
  return isTokenExpired(token, REFRESH_BUFFER);
};

/**
 * Validate token structure (not signature - server does that)
 * 
 * @param token - JWT token
 * @returns True if token has valid structure
 */
export const isValidTokenStructure = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false;
  
  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  // Try to decode
  const payload = decodeToken(token);
  return payload !== null && !!payload.exp;
};

/**
 * Get expiry timestamp from token
 * 
 * @param token - JWT token
 * @returns Unix timestamp (seconds) or null if invalid
 */
export const getTokenExpiry = (token: string): number | null => {
  const payload = decodeToken(token);
  return payload?.exp ?? null;
};

/**
 * Calculate expires_in (seconds until expiry) from JWT token
 * 
 * Reads the `exp` claim from the JWT and calculates how many seconds
 * until it expires from the current time.
 * 
 * @param token - JWT access token
 * @returns Seconds until expiry, or null if token is invalid/expired
 * 
 * @example
 * ```ts
 * const expiresIn = getTokenExpiresIn(accessToken);
 * if (expiresIn) {
 *   console.log(`Token expires in ${expiresIn} seconds`);
 * }
 * ```
 */
export const getTokenExpiresIn = (token: string): number | null => {
  const payload = decodeToken(token);
  if (!payload?.exp) return null;
  
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const expiresIn = payload.exp - now;
  
  // Return null if token is already expired
  return expiresIn > 0 ? expiresIn : null;
};
